import { Dispatch } from 'redux';
import { WorkingInterview } from './Constants';
import { TGetState } from '~/store/types';

interface IRawWorkingInterview {
  breakTime: string | null;
  dates: Date[];
  hourlyRate: string;
  hoursArrival: string;
  hoursDeparture: string;
  isAssistedHygiene: boolean;
  isPaidBreakTime: boolean;
  specialty: string;
}

export interface IAppendWorkingInterviewPayload {
  jobPostingId: string;
  data: IRawWorkingInterview;
}

/**
 * Action creator for appending working interviews
 */
export const appendWorkingInterviews =
  (jobPostingId: string, payload: IRawWorkingInterview) => (dispatch: Dispatch) => {
    dispatch({
      type: WorkingInterview.APPEND,
      payload: {
        data: payload,
        jobPostingId,
      } satisfies IAppendWorkingInterviewPayload,
    });
  };

export interface ICreateWorkingInterviewPayload {
  postingId: string;
  workingInterviews: {
    breakTime: string | null;
    dates: Date[];
    hourlyRate: string;
    hoursArrival: string;
    hoursDeparture: string;
    isAssistedHygiene: boolean;
    isPaidBreakTime: boolean;
    specialty: string;
  };
}

/**
 * Action creator for creating working interviews
 */
export const createWorkingInterviews =
  (id: string, payload: ICreateWorkingInterviewPayload['workingInterviews']) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: WorkingInterview.CREATE,
      payload: {
        postingId: id,
        workingInterviews: payload,
      } satisfies ICreateWorkingInterviewPayload,
    });
  };

export interface IDeleteWorkingInterviewPayload {
  id: string;
  jobPostingId: string;
}

export const deleteWorkingInterview =
  ({ id, jobPostingId }: IDeleteWorkingInterviewPayload) =>
  (dispatch: Dispatch, getState: TGetState) => {
    const { workingInterviews } = getState().permanentJobs;

    const interviews = workingInterviews[jobPostingId];

    if (!interviews || interviews?.length <= 1) return;

    const isExist = interviews.findIndex((it) => it.id === id) !== -1;

    if (!isExist) return;

    dispatch({
      type: WorkingInterview.DELETE,
      payload: {
        id,
        jobPostingId,
      } satisfies IDeleteWorkingInterviewPayload,
    });
  };

export interface IEditWorkingInterviewPayload {
  postingId: string;
  workingInterview: {
    breakTime: string | null;
    hourlyRate: string;
    hoursArrival: string;
    hoursDeparture: string;
    id: string;
    isAssistedHygiene: boolean;
    isPaidBreakTime: boolean;
    specialty: string;
  };
}

/**
 * Action creator for editing working interview
 */
export const editWorkingInterview =
  (payload: IEditWorkingInterviewPayload['workingInterview'], postingId: string) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: WorkingInterview.EDIT,
      payload: {
        postingId,
        workingInterview: payload,
      } satisfies IEditWorkingInterviewPayload,
    });
  };

export interface IClearWorkingInterviewsPayload {
  postingId: string;
}

/**
 * Action creator for clearing all working interviews for a posting
 */
export const clearWorkingInterviews = (postingId: string) => (dispatch: Dispatch) => {
  dispatch({
    type: WorkingInterview.CLEAR,
    payload: {
      postingId,
    } satisfies IClearWorkingInterviewsPayload,
  });
};
